import React from "react";
import {
    Navigate
} from "react-router-dom";
import {clearSession, getAccessToken} from "./authority";
import {
    clearPermissionCache,
    ensurePermissionsLoaded,
    hasPermissionCode,
    isPermissionLoaded,
    startPermissionAutoRefresh,
    subscribePermissions
} from "./permissionStore";
import {jwtDecode} from "jwt-decode";
import pako from "pako";

/**
 * Kiểm tra quyền của user hiện tại.
 *
 * SCR-070: nguồn quyền là `GET /user/my-permission` (đọc `role_permission` lúc gọi), giữ trong
 * `permissionStore`. Trước đây hàm này giải mã claim `authorities` của JWT — ảnh chụp lúc đăng
 * nhập, token sống 7 ngày và không có refresh token, nên sửa quyền phải bắt đăng nhập lại
 * mới thấy; server thì đã gác theo DB ⇒ menu hiện mà bấm vào 403.
 *
 * Vẫn lùi về claim trong token khi store CHƯA kịp nạp (tab mở từ bản CMS cũ, render trước
 * khi `RequireAuth` nạp xong): thà hiện theo quyền cũ còn hơn trắng menu.
 *
 * @param {string} permission - Quyền cần kiểm tra (có thể chứa "|" để check nhiều quyền)
 * @returns {boolean} True nếu user có quyền
 */
export function checkPermission(permission) {
    const token = getAccessToken();
    if (token === undefined){
        return false;
    }
    if (!permission) return false;

    const permissionArr = permission.split("|").map(p => p.trim()).filter(Boolean);

    // So khop TUYET DOI (khong dung indexOf): "S1" tung khop nham chuoi con cua "SMS1",
    // khien nut/menu hien sai so voi @PreAuthorize cua server (server dung equals).
    if (isPermissionLoaded()) {
        return permissionArr.some(p => hasPermissionCode(p));
    }

    try {
        const decodedToken = jwtDecode(token);
        const list_permission = decodeBase64(decodedToken["authorities"]);
        if (!list_permission) return false;
        const grantedPermissions = new Set(
            list_permission
                .replace(/^\[|\]$/g, "")
                .split(",")
                .map(p => p.trim())
                .filter(Boolean)
        );
        return permissionArr.some(p => grantedPermissions.has(p));
    } catch (error) {
        console.error("Error checking permission:", error);
        return false;
    }
}
/**
 * `schoolid` trong JWT — trường của phiên đăng nhập.
 *
 * Tài khoản KHÔNG gắn trường (admin tổng) được server phát `-1`
 * (`UserDetailsServiceImpl`: `userEntity.getSchool() != null ? id : -1`).
 * @returns {number|null} null nếu không có token / token hỏng.
 */
export function getTokenSchoolId() {
    const token = getAccessToken();
    if (!token) return null;
    try {
        const value = jwtDecode(token)["schoolid"];
        if (value === undefined || value === null || value === "") return null;
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? null : parsed;
    } catch (error) {
        return null;
    }
}

/**
 * Phiên hiện tại là ADMIN TỔNG (không thuộc trường nào)?
 *
 * Dùng để bỏ các ràng buộc chỉ có nghĩa với người dùng của MỘT trường:
 * không ép chọn năm học khi đăng nhập, không ghim tên trường trên header.
 * Admin tổng làm việc với dữ liệu hợp nhất, năm học là THAM SỐ của từng báo cáo
 * (xem ba-shared/api-contracts/system-report/README.md), không phải ngữ cảnh phiên.
 *
 * `< 0` chứ không phải `<= 0`: token phụ huynh mang `schoolid = 0`
 * (nhánh PH không bao giờ gọi `setSchoolId`, `UserCustom.schoolId` là `int`), xem SCR-052 §4.
 *
 * ⚠️ Đây chỉ là gate GIAO DIỆN. Quyền thật do server chốt (`hasPermission` +
 * chốt role ADMIN_SYSTEM trong service) — đừng coi hàm này là hàng rào bảo mật.
 */
export function isSystemScope() {
    const schoolId = getTokenSchoolId();
    return schoolId !== null && schoolId < 0;
}

export function decodeBase64(base64String) {
    try {
        // Giải mã Base64
        const binaryString = atob(base64String);
        const binaryData = Uint8Array.from(binaryString, char => char.charCodeAt(0));

        // Giải nén GZIP
        const decompressedData = pako.inflate(binaryData, { to: 'string' });
        return decompressedData;
    } catch (error) {
        return null;
    }
}
/**
 * Dọn phiên cho nhánh KHÔNG reload trang (token hết hạn/hỏng — `<Navigate/>` lo điều hướng).
 *
 * SCR-070: phải xoá luôn danh sách quyền đang giữ trong RAM của `permissionStore`, không chỉ
 * `localStorage`. Không reload thì module không khởi tạo lại, nên người đăng nhập tiếp theo trên
 * cùng tab sẽ render menu theo quyền của người trước cho tới khi `my-permission` trả về.
 * (Nhánh bấm Đăng xuất / 401 đi qua `forceLogout` — `window.location.replace` nên RAM tự sạch.)
 */
function endSession() {
    clearSession();
    clearPermissionCache();
}

/**
 * Phiên còn hiệu lực? Kiểm tra luôn claim exp trong JWT (TTL server 7 ngày,
 * xem ba-shared/api-contracts/auth/session-expiry.md) thay vì chỉ xem token có
 * tồn tại hay không — nếu không, user mở tab với token chết vẫn vào được trang
 * rồi mới bị đá ra khi API đầu tiên trả 401.
 * Token hết hạn thì dọn luôn phiên; phần điều hướng để <Navigate/> lo.
 */
function hasValidSession() {
    const token = getAccessToken();
    if (!token) return false;
    try {
        const { exp } = jwtDecode(token);
        if (exp && exp * 1000 <= Date.now()) {
            endSession();
            return false;
        }
    } catch (error) {
        // Token hỏng/không decode được — coi như không có phiên.
        endSession();
        return false;
    }
    return true;
}

/**
 * Spinner trong lúc nạp quyền lần đầu (đăng nhập mới, chưa có bản chụp trong localStorage).
 * Thường chỉ thấy trong ~200ms; lần sau vào là render ngay từ bản chụp.
 */
function PermissionLoading() {
    return (
        <div className="d-flex justify-content-center align-items-center" style={{height: '100vh'}}>
            <div className="text-center text-muted">
                <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div>Đang tải quyền truy cập…</div>
            </div>
        </div>
    );
}

/**
 * Cổng vào mọi trang nội bộ: còn phiên + ĐÃ có danh sách quyền mới cho render.
 *
 * SCR-070 — vì sao phải chặn render: `checkPermission()` là hàm đồng bộ, được gọi ngay trong
 * `render()` của ~253 chỗ (kể cả bảng route trong `routes/admin/index.js`). Render trước khi
 * quyền về thì route bị loại khỏi cây và người dùng thấy "Không tìm thấy trang" dù có quyền.
 *
 * Có bản chụp cũ trong localStorage thì render ngay, làm mới ở phía sau; quyền đổi thì
 * `subscribePermissions` bắn tín hiệu để render lại cây con.
 */
export function RequireAuth({ children, redirectTo }) {
    const authenticated = hasValidSession();
    const [ready, setReady] = React.useState(() => isPermissionLoaded());
    const [, setVersion] = React.useState(0);

    React.useEffect(() => {
        if (!authenticated) return undefined;
        let alive = true;
        ensurePermissionsLoaded().finally(() => {
            if (alive) setReady(true);
        });
        startPermissionAutoRefresh();
        const unsubscribe = subscribePermissions(() => {
            if (alive) setVersion(v => v + 1);
        });
        return () => {
            alive = false;
            unsubscribe();
        };
    }, [authenticated]);

    if (!authenticated) return <Navigate to={redirectTo} replace />;
    // Nạp quyền lỗi (mạng rớt) mà cũng không có bản chụp: vẫn cho vào, `checkPermission`
    // lùi về claim trong token — chặn ở đây là người dùng kẹt vĩnh viễn ở spinner.
    if (!ready) return <PermissionLoading />;
    return children;
}


export function RedirectAuth({ children, redirectTo }) {
    return hasValidSession() ? <Navigate to={redirectTo} replace />: children;
}
export function changeAlias(alias) {
    if(alias) {
        let str = alias;
        str = str.toLowerCase();
        str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, "a");
        str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, "e");
        str = str.replace(/ì|í|ị|ỉ|ĩ/g, "i");
        str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, "o");
        str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, "u");
        str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, "y");
        str = str.replace(/đ/g, "d");
        str = str.replace(/!|@|%|\^|\*|\(|\)|\+|\=|\<|\>|\?|\/|,|\.|\:|\;|\'|\"|\&|\#|\[|\]|~|\$|_|`|-|{|}|\||\\/g, " ");
        str = str.replace(/ + /g, " ");
        str = str.trim();
        return str;
    }
    return alias;
}
